/**
 * Donor Bloc Constants
 *
 * Defines the donor groups that parties can solicit for fundraising.
 * Each bloc has ideological preferences that affect donation success
 * and potential political consequences.
 */

import type { IdeologyVector } from './ideology'

export type DonorBlocKey = 'business' | 'labor' | 'grassroots' | 'wealthy'

export interface DonorBloc {
  key: DonorBlocKey
  icon: string
  label: string
  description: string
  /** Ideal ideology vector - party alignment is calculated against this */
  idealIdeology: IdeologyVector
  /** Base money raised per AP at 100% alignment */
  baseYieldPerAP: number
  /** Variance in yield (actual = base +/- variance) */
  yieldVariance: number
  /** AP range allowed for this bloc [min, max] */
  apRange: [number, number]
}

/**
 * Donor bloc definitions with ideology profiles and yield parameters.
 *
 * Ideology axes (for reference):
 * 0: economic_regulation (-100 free market, +100 heavy regulation)
 * 1: social_values (-100 traditional, +100 progressive)
 * 2: civil_liberties (-100 restrictive, +100 permissive)
 * 3: national_identity (-100 nationalist, +100 cosmopolitan)
 * 4: environment (-100 development, +100 green)
 * 5: foreign_security (-100 isolationist, +100 interventionist)
 * 6: government_centralization (-100 decentralized, +100 centralized)
 */
export const DONOR_BLOCS: Record<DonorBlocKey, DonorBloc> = {
  business: {
    key: 'business',
    icon: '🏢',
    label: 'Business',
    description: 'Corporate donors and business associations',
    // Prefer: low regulation, development over environment, moderate centralization
    idealIdeology: [-60, 0, 20, 0, -40, 0, 20],
    baseYieldPerAP: 12000,
    yieldVariance: 3000,
    apRange: [1, 3],
  },
  labor: {
    key: 'labor',
    icon: '👷',
    label: 'Labor',
    description: 'Unions and worker organizations',
    // Prefer: high regulation, progressive values, green, isolationist
    idealIdeology: [60, 40, 0, -20, 20, -20, 0],
    baseYieldPerAP: 7500,
    yieldVariance: 2500,
    apRange: [1, 3],
  },
  grassroots: {
    key: 'grassroots',
    icon: '🌱',
    label: 'Grassroots',
    description: 'Small donors and community supporters',
    // Neutral - grassroots care about momentum, not ideology
    idealIdeology: [0, 0, 0, 0, 0, 0, 0],
    baseYieldPerAP: 5500,
    yieldVariance: 2500,
    apRange: [1, 3],
  },
  wealthy: {
    key: 'wealthy',
    icon: '💎',
    label: 'Wealthy Individuals',
    description: 'High-net-worth donors and philanthropists',
    // Prefer: low regulation, permissive liberties, cosmopolitan, low centralization
    idealIdeology: [-50, 20, 50, 30, -20, 20, -20],
    baseYieldPerAP: 15000,
    yieldVariance: 5000,
    apRange: [1, 3],
  },
} as const

/**
 * Get ordered list of donor bloc keys for consistent UI rendering
 */
export const DONOR_BLOC_ORDER: DonorBlocKey[] = [
  'business',
  'labor',
  'grassroots',
  'wealthy',
]

/**
 * Intensity level descriptions for fundraising
 */
export const FUNDRAISE_INTENSITY_LABELS: Record<number, string> = {
  1: 'Casual outreach',
  2: 'Active solicitation',
  3: 'Major push',
}
